import { cn } from "@nexus/ui";
import { industryLabel } from "./OrgCard";
import { OrgLogoMark } from "./OrgLogoMark";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";
import {
  orgAccentRowSurfaceStyle,
  orgAccentRowWashStyle,
  orgAccentRowWashStyleDark,
  orgAccentRowWashStyleDarkHover,
} from "@/features/identity-hub/utils/org-accent-surface";
import type { OrgPickerItem } from "./OrgPickerCombobox";

export const ORG_PICKER_ITEM_CLASS = cn(
  "group/org-opt mb-1 cursor-pointer overflow-hidden border transition-[border-color,box-shadow] duration-150",
  "hover:border-[color-mix(in_srgb,var(--org-accent)_38%,var(--border))] hover:shadow-sm",
  "dark:hover:border-[color-mix(in_srgb,var(--org-accent)_48%,var(--border))]",
  "dark:hover:shadow-[0_0_0_1px_color-mix(in_srgb,var(--org-accent)_28%,transparent),0_2px_8px_-2px_color-mix(in_srgb,var(--org-accent)_22%,transparent)]",
  "data-[selected=true]:border-[color-mix(in_srgb,var(--org-accent)_42%,var(--border))] data-[selected=true]:shadow-sm",
  "dark:data-[selected=true]:border-[color-mix(in_srgb,var(--org-accent)_55%,var(--border))]",
  "data-[selected=true]:bg-transparent data-[selected=true]:text-foreground",
  "[&>svg]:relative [&>svg]:z-[1]",
);

/** Color-free item — mirrors the nav dropdown-menu item: neutral highlight fill, no org accent. */
export const ORG_PICKER_ITEM_PLAIN_CLASS = cn(
  "group/org-opt relative mb-0.5 cursor-pointer overflow-hidden rounded-[10px] p-2.5 text-foreground",
  "transition-colors duration-150",
  // Highlighted row surface (cmdk active-descendant) — match the dropdown-menu's quiet neutral fill.
  // Override CommandItem's default primary-subtle highlight (bg + text) to stay neutral.
  "data-[selected=true]:bg-[color-mix(in_srgb,var(--popover),var(--foreground)_4%)] data-[selected=true]:text-foreground",
  "dark:data-[selected=true]:bg-[color-mix(in_srgb,var(--popover),var(--foreground)_8%)]",
  "motion-reduce:transition-none",
);

export const ORG_PICKER_POPOVER_CLASS = cn(
  "flex w-(--radix-popover-trigger-width) max-h-[min(var(--radix-popover-content-available-height),36rem)] flex-col overflow-hidden p-0",
  // Match the nav dropdown-menu container: neutral hairline, popover surface, single DESIGN.md shadow tier.
  "rounded-[12px] border border-border bg-popover",
  "shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]",
  // SearchableCombobox renders an inner Command with bg-muted — keep the interior on the popover surface to match.
  "[&_[data-slot=command]]:bg-popover",
);

export function OrgTypeBadge({ type }: { type?: string }) {
  if (!type) return null;
  return (
    <span className="shrink-0 rounded bg-muted/80 px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
      {industryLabel(type)}
    </span>
  );
}

/** Compact badge showing the current user's role within an org row. */
export function OrgRoleBadge({ label }: { label: string }) {
  return (
    <span className="shrink-0 rounded border border-border/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
      {label}
    </span>
  );
}

export function OrgPickerOptionContent({
  org,
  showTypeBadge = true,
  accent: showAccent = true,
  roleLabel,
}: {
  org: OrgPickerItem;
  showTypeBadge?: boolean;
  /** When false, drop all org accent washes/colors and render a neutral row. */
  accent?: boolean;
  /** Optional trailing badge showing the current user's role in this org. */
  roleLabel?: string | null;
}) {
  const accent = tenantAccentColor(org);

  return (
    <>
      {showAccent ? (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-sm opacity-85 transition-opacity duration-150 group-hover/org-opt:opacity-100 group-data-[selected=true]/org-opt:opacity-100 dark:hidden"
            style={orgAccentRowWashStyle(accent)}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden rounded-sm dark:block"
            style={orgAccentRowWashStyleDark(accent)}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden rounded-sm opacity-0 transition-opacity duration-150 group-hover/org-opt:opacity-100 group-data-[selected=true]/org-opt:opacity-100 dark:block"
            style={orgAccentRowWashStyleDarkHover(accent)}
          />
        </>
      ) : null}
      <div className="relative z-[1] flex min-w-0 flex-1 items-center gap-2.5">
        <OrgLogoMark
          name={org.name}
          logoUrl={org.logoUrl}
          accent={showAccent ? accent : undefined}
          neutral={!showAccent}
          isSuperTenant={org.type === "SUPER_TENANT"}
          size={20}
        />
        <span className="min-w-0 flex-1 truncate text-[13px] tracking-[-0.006em]">{org.name}</span>
        {showTypeBadge ? <OrgTypeBadge type={org.type} /> : null}
        {roleLabel ? <OrgRoleBadge label={roleLabel} /> : null}
      </div>
    </>
  );
}

export function orgPickerItemStyle(org: OrgPickerItem) {
  return orgAccentRowSurfaceStyle(tenantAccentColor(org));
}
